import { alpha } from "@mui/material/styles";
import { Avatar, Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ColorModeContext, tokens } from "../theme";
import { 
  LightModeOutlined, 
  DarkModeOutlined, 
  PersonOutlined, 
  Menu as MenuIcon, 
  Logout,
  ArrowBack
} from "@mui/icons-material";
import SignOutDialog from "./Authentication/SignOutDialog";
import NotificationBarDialog from "../scenes/incident/NotificationBarDialog";
import { getUserFromCookies } from "../utils/Cookie-utils";


const Topbar = ({ setIsSidebarOpen }) => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const colors = tokens(theme.palette.mode);
  const [incidentCount, setIncidentCount] = useState(0);
  const [userId, setUserId] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const user = getUserFromCookies();
  const userName = user?.data?.name || "User";

  const getToolbarTitle = () => {
    const path = location.pathname;
    const titleMap = {
      dashboard: "Dashboard",
      teacher_details: "Teacher Details",
      profileDetails: "Profile Details",
      "user-details": "User Details",
      students: "Students",
      student_details: "Student Details",
      parents: "Parents",
      teachers: "Teachers",
      users: "Users",
      classes: "Classes",
      subjects: "Subjects",
      terms: "Terms",
      examinations: "Examinations",
      results: "Results",
      sessions: "Sessions",
      session: "Sessions",
      "session-time-ranges": "Session Time Ranges",
      "teachersDuty": "Teacher Duty",
      "classes-for-timetable": "Class Timetable",
      schoolTimetable: "School Timetable",
      "duties-weeks": "Duties Weeks",
      "seminarian_information": "Seminarian Information",
      "ada-michango": "Student Accounts",
      "student-evaluation": "Student Evaluation",
      "student-evaluation-report": "Student Evaluation Report",
      "student-rector-comments": "Rector Comments",
      request: "Requests",
      requests: "Requests",
      incidents: "Incidents",
      announcements: "Announcements",
      "parents-announcements": "Matangazo",
      "teachers-announcements": "Announcements",
    };
    const isUuidLike = (value) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

    if (path === "/dashboard") return "Dashboard";

    const segments = path
      .split("/")
      .filter(Boolean)
      .filter((segment) => !/^\d+$/.test(segment))
      .filter((segment) => !segment.includes(":"))
      .filter((segment) => !isUuidLike(segment));

    const mappedSegment = segments.find((segment) => titleMap[segment]);
    const lastSegment = mappedSegment || segments[segments.length - 1] || "dashboard";

    return titleMap[lastSegment] || lastSegment
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  useEffect(() => {
    if (user?.data?.id) {
      setUserId(user.data.id);
    }
  }, [user]);

  const handleIncidentCountUpdate = (count) => setIncidentCount(count);
  
  const handleLogoutDialogOpen = () => setLogoutDialogOpen(true);
  const handleLogoutDialogClose = () => setLogoutDialogOpen(false);
  const handleLogoutConfirm = () => setLogoutDialogOpen(false);
  
  const handleNotificationDialogOpen = () => setNotificationDialogOpen(true);
  const handleNotificationDialogClose = () => setNotificationDialogOpen(false);
  
  const handleUserDetailsOpen = () => {
    if (userId) {
      navigate(`/profileDetails/${userId}`);
    } else {
      console.error("User ID not found");
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleGoBack = () => {
    navigate(-1); // Navigate to previous page
  };

  // Check if we should show the back button (not on dashboard or main pages)
  const shouldShowBackButton = () => {
    const mainPages = ['/dashboard', '/home', '/login', '/', '/resetPassword', '/notFound', '/register'];
    return !mainPages.includes(location.pathname);
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          p: { xs: 1.5, md: 2 },
          m: { xs: 1, md: 2 },
          mb: 1,
          borderRadius: "18px",
          backgroundColor: colors.primary[400],
          border: `1px solid ${alpha(colors.grey[100], 0.08)}`,
          backdropFilter: "blur(10px)",
          boxShadow: "0 12px 28px rgba(0, 0, 0, 0.18)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.25}>
          {/* Back Button - Show only on non-main pages */}
          {shouldShowBackButton() && (
            <IconButton
              onClick={handleGoBack}
              aria-label="Go back"
              sx={{
                backgroundColor: alpha(colors.blueAccent[500], 0.24),
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: alpha(colors.blueAccent[500], 0.36),
                },
                borderRadius: '12px',
                p: 1,
                mr: 1
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          
          {/* Menu Button */}
          <IconButton 
            onClick={toggleSidebar} 
            aria-label="Toggle sidebar"
            sx={{
              backgroundColor: alpha(colors.blueAccent[500], 0.24),
              color: colors.grey[100],
              '&:hover': {
                backgroundColor: alpha(colors.blueAccent[500], 0.36),
              },
              borderRadius: '12px',
              p: 1
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ ml: 0.5, display: { xs: "none", sm: "block" }, color: colors.grey[100] }}
          >
            {getToolbarTitle()}
          </Typography>

        </Box>
        
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1, mr: 1 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: colors.blueAccent[500], color: colors.grey[100] }}>
              {userName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ color: colors.grey[100] }}>
                {userName}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                Welcome back
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Toggle theme">
            <IconButton onClick={colorMode.toggleColorMode} aria-label="Toggle color mode" sx={{ color: colors.grey[100] }}>
            {theme.palette.mode === "dark" ? <DarkModeOutlined /> : <LightModeOutlined />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Profile">
            <IconButton onClick={handleUserDetailsOpen} aria-label="User profile" sx={{ color: colors.grey[100] }}>
            <PersonOutlined />
            </IconButton>
          </Tooltip> 
          <Tooltip title="Logout">
            <IconButton onClick={handleLogoutDialogOpen} aria-label="Logout" sx={{ color: colors.grey[100] }}>
            <Logout />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <SignOutDialog
        open={logoutDialogOpen}
        onClose={handleLogoutDialogClose}
        onConfirm={handleLogoutConfirm}
        setLogoutDialogOpen={setLogoutDialogOpen}
      />

      <NotificationBarDialog
        open={notificationDialogOpen}
        onClose={handleNotificationDialogClose}
        onIncidentCountUpdate={handleIncidentCountUpdate}
      />
    </>
  );
};

export default Topbar;